import { useResource } from '@landsbankinn/l16n';
import React, { createContext, useContext, useState } from 'react';
import { useApplicationServerContext } from '../../../../../state';
import { client as prismicClient } from './prismic';

import * as prismic from '@prismicio/client';
import { internal } from 'src/services/ConsoleLoggerService';
import { isCorporate } from '../../../../isCorporate';

type MarketingContentData = {
  ad: { imageUrl: string; text: string; linkUrl: string };
};

type PrismicSliceProductCards = prismic.Slice<
  'product_cards',
  Record<string, never>,
  {
    copy: prismic.RichTextField;
    heading: prismic.RichTextField;
    image: prismic.ImageField<'ar_1_1'>;
    link: prismic.LinkField;
  }
>;

type PrismicSliceText = prismic.Slice<'text', { text: prismic.RichTextField }>;

type PrismicPage = prismic.PrismicDocument<{
  body: [PrismicSliceProductCards, PrismicSliceText, PrismicSliceText];
}>;

const MarketingContentContext = createContext<MarketingContentData | undefined>(
  undefined,
);
export const PrismicContentContextProvider = (props: {
  children: React.ReactNode;
}) => {
  const [data, setData] = useState<MarketingContentData | undefined>();
  const { locale } = useResource();
  const [serverState] = useApplicationServerContext();

  React.useEffect(() => {
    // Determine if this is the corporate client
    const isCorporateClient = isCorporate(serverState.clientName['is']);

    const fetchData = async () => {
      // Using the prismic client to fetch https://landsbankinn.is/idp-is page
      // This page is stored in the Prismic CMS and provides images/text/links to display
      // as marketing content.
      const pageType = isCorporateClient ? 'idp-fbl' : 'idp';
      const pageName = locale ? `${pageType}-${locale}` : `${pageType}-is`;
      const document = await prismicClient.getByUID<PrismicPage>(
        'page',
        pageName,
      );
      const slices = document.data.body;

      // Fetching data this manually is by no means correct and is only for temporary use until
      // we can use @landsbankinn/prismic and @landsbankinn/slices in the IDP UI project
      // so the same UI components are used between landsbankinn.is and the IDP UI application.
      const productCards = slices[0].items[0];

      const data: MarketingContentData = {
        ad: {
          imageUrl: productCards.image.ar_1_1.url || '',
          text: prismic.asText(productCards.copy) || '',
          linkUrl: prismic.asLink(productCards.link) || '',
        },
      };

      setData(data);
    };

    // Fetch data and/or show predefined data on error
    fetchData().catch((err) => {
      internal.warn(err);
      // This data is used if fetching content from prismic failes
      setData({
        ad: isCorporateClient
          ? {
              imageUrl:
                'https://images.prismic.io/landsbankinn/161b3dc7-c253-4e20-beb1-441d197b957c_2021-08-27-Taeknimenn.jpg?auto=compress,format',
              text: 'Við bjóðum fyrirtæki velkomin í Landsbankaappið',
              linkUrl: 'https://www.landsbankinn.is/fyrirtaeki/appid',
            }
          : {
              imageUrl:
                'https://images.prismic.io/landsbankinn/0d154aff-96d9-430b-985b-92cb8206d356_Landsbankinn_Uloma_V4A0441.png?auto=compress,format',
              text: 'Pantaðu tíma fyrir ráðgjöf og þjónustu',
              linkUrl: 'https://www.landsbankinn.is/panta-tima',
            },
      });
    });
  }, [locale, serverState]);

  return (
    <MarketingContentContext.Provider value={data} children={props.children} />
  );
};

export function usePrismicContent() {
  const data = useContext(MarketingContentContext);
  return data;
}
